import { Component, HostListener, OnInit } from '@angular/core';
import { LocalStorageService } from './shared/local-storage.service';
import { LookupDataService } from './shared/lookup-data.service';
import { AuthService } from './shared/auth.service';
import { catchError, timeout } from 'rxjs/operators';
import { ActivatedRoute, Router, RoutesRecognized } from '@angular/router';
import { OnlineStatusService, OnlineStatusType } from 'ngx-online-status';
import { ToastrService } from 'ngx-toastr';
import { OperatorUser } from './shared/operator-user.model';
import { throwError } from 'rxjs';
import shpjs from 'shpjs';
import { Insights } from './shared/insights.service';
import { ConflictResolverService } from './shared/conflict-resolver.service';
import { environment } from 'src/environments/environment';
import { PwaUpdateService } from './shared/update-service.service';

/**
 * App/Root component
 */
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
    standalone: false
})
export class AppComponent implements OnInit {
  title = 'psmfc-elog';
  displayComponent: boolean = false;
  displayTestBanner: boolean = !environment.production;

  private readonly REQUEST_TIMEOUT_MS = 3 * 1000;
  /**
   * In the app component constructor, wait for the app to stabilize then check for and apply updates. 
   * @param updates Angular service to check for new versions of the app
   * @param appRef To check app stability before applying updates
   */
  constructor(
    public dataSync: LocalStorageService,
    private auth: AuthService,
     private router: Router,
     private toastr: ToastrService,
      public route: ActivatedRoute,
      private onlineStatusService: OnlineStatusService,
       private lookup: LookupDataService,
       private updater: PwaUpdateService,
        private insights: Insights) {

      router.events.subscribe(event => {
        if (event instanceof RoutesRecognized) {
          if (this.dataSync.user_storage && 
            this.onlineStatusService.getStatus() == OnlineStatusType.ONLINE &&
            this.dataSync.logged_in()) this.dataSync.syncUserDatabase(true);
        }
      });

      ConflictResolverService.insights = this.insights;
    }


    @HostListener('document:visibilitychange', ['$event'])
    visibilitychange() {
      if (this.dataSync.user_storage && 
        this.dataSync.logged_in() && 
        this.onlineStatusService.getStatus() == OnlineStatusType.ONLINE) this.dataSync.syncUserDatabase(true);
    }


    /**
     * The 'first' thing to run whenever the app loads or is refreshed.
     * Check if a user is still logged in and perform the login sequence if they are.
     */
  ngOnInit(): void {
    console.log('Init app');
    
    this.onCheckUpdate();
    
    //We check the login_status document in storage for a still logged in user
    this.dataSync.openUserAccounts().then(() => {
      //If logged in, refresh access token and retrieve all data from DB
      //If refresh token has expired then logout and go to login screen
      this.insights.trackEvent('Open accounts', 'Accounts opened on app init');

      if (this.dataSync.logged_in()) {
        this.insights.trackEvent('Auto login', 'User already logged in');
        if (this.onlineStatusService.getStatus() === OnlineStatusType.ONLINE && this.auth.checkRefreshToken().expired) {
          console.log('Refresh token expired');
          this.toastr.warning('Login session has expired. Log in again to refresh', 'Session Expired')
          this.dataSync.logged_in.set(false);
          this.dataSync.local_storage.get('login_status').then((doc: any) => {
            doc.user = '';
            doc.isLoggedIn = false;
            this.dataSync.local_storage.put(doc);
            this.router.navigate(['login']);
            this.displayComponent = true;
          });
          this.dataSync.cleanupStorage();         
        } else {
          //Refresh token is not expired, user is offline, or both
          if (this.onlineStatusService.getStatus() === OnlineStatusType.ONLINE) {
            //Refresh token has not expired            

            //Check for a migration, open storage, then sync
            this.dataSync.checkForMigration().then((res: boolean) => {

              if (!res) {
                //Need to perform a migration
                this.insights.trackEvent('HMS Migration', 'Need to migrate to HMS');
                this.dataSync.migrateToHMSCombined().then(() => {
                //We need to get this user's couchDB connection, so try refreshing the token first
                this.auth.postRefreshToken().subscribe(result => {
                  console.log("Token refresh for couchDB account creation");
                    this.dataSync.openLocalStorage();
                    this.dataSync.storage_promise.then(() => {
                      this.dataSync.syncUserDatabase();
                      this.displayComponent = true;
                      this.dataSync.sync_promise.then(() => this.afterMigrationCheck());
                    }).catch(_ => {
                      this.displayComponent = true;
                      this.logoutUser();
                    });  
                  });                 
                }, _ => {
                  this.displayComponent = true;
                  this.logoutUser()
                });
                
              } else {
                this.insights.trackEvent('App init open database', 'Initial database opening');
                this.dataSync.openLocalStorage();
                this.dataSync.storage_promise.then(() => {
                  if (this.dataSync.current_user().couchDbConnection == "") {
                    //Need to get the user couchDB connection
                    this.auth.postRefreshToken().subscribe({
                      next: (response) => {
                      console.log("Token refresh for couchDB account creation");
                      this.dataSync.current_user.update(user => {user.couchDbConnection = response.couchDbConnection; return user});                      
                      this.displayComponent = true;
                      this.afterMigrationCheck(); 
                      this.dataSync.syncUserDatabase(true);
                      }, 
                      error: () => this.logoutUser()});
                  } else {   
                    this.displayComponent = true;
                    this.afterMigrationCheck();
                    this.dataSync.syncUserDatabase(true);
                  }   
                });
                                                      
              }
            })          
         
          } else {
            //User is not online and might have expired refresh token      
            this.dataSync.openLocalStorage();
            this.dataSync.storage_promise.then(() => {
              this.displayComponent = true;
              this.lookup.retrieveLookupData();
              if (this.router.url == '/login') this.router.navigate(['menu']);
            }).catch(() => {
              console.log('Error in offline open user storage');
              this.lookup.retrieveLookupData();
              if (this.router.url == '/login') this.router.navigate(['menu']);
            })
                       
          }
        }
        
      } else {
        this.displayComponent = true;
        console.log('User is not logged in');
        this.router.navigate(['login']);
      }
    });

    fetch("../../assets/EEZ_WestCoast_PacVes_shapefile.zip")
                .then(text => text.arrayBuffer())
                .then((myshape) => shpjs(myshape))
                .then((data: any) => {
                        this.dataSync.west_coast_eez.set(data.features[0].geometry);
                }).catch(e => {
                  this.insights.trackException(e);
                  this.insights.trackTrace("Failed to retrieve the shapefile");
                });
  }


  /**
   * Set the tokens and retrieve lookup data the go to the main menu.
   */
  afterMigrationCheck() {
    if (this.router.url == '/login') this.router.navigate(['menu']);
    if (this.auth.checkAccessToken().expired) {
      this.auth.postRefreshToken().pipe(timeout(this.REQUEST_TIMEOUT_MS), catchError((error) => {
        if (error.name === 'TimeoutError') console.error('Request timed out.');
        return throwError(error);
      }))   
      .subscribe({
        next: (res) => {
          let user = this.dataSync.current_user();
          user.accessToken = res.accessToken;
          user.refreshToken = res.refreshToken;
          this.dataSync.current_user.set(user);
          this.dataSync.accessToken.set(res.accessToken);
          this.dataSync.refreshToken.set(res.refreshToken);
          this.dataSync.updateUser(false, true);
          this.lookup.retrieveLookupData();
      },
      error: () => this.lookup.retrieveLookupData()});
    } else {
      this.lookup.retrieveLookupData();     
    }   
  }



  async onCheckUpdate(): Promise<void> {
    const available = await this.updater.checkForUpdate();
    if (available)  {
      this.toastr.success('New version found, reloading...', 'Update ready');
      await this.updater.activateUpdate();
    }

  }



  /**
   * Update login status and go back to login.
   */
  logoutUser() {
    //If error then we have to log the user out   
    this.insights.trackEvent('Logging out user', 'Logging out the current user');               
    this.dataSync.logged_in.set(false);
    this.dataSync.local_storage.upsert('login_status', (doc: any) => {
      doc.user = '';
      doc.isLoggedIn = false;
      return doc;
    }).then(() => this.insights.trackEvent('Missing logn status doc', 'Upserted login status document'))
    .catch(err => this.insights.trackException(err));

    this.dataSync.current_user.set(new OperatorUser());
    this.router.navigate(['login'])
  }
}
